import React, { useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Typography,
} from "@material-ui/core";

// Compara dos valores de celda para ordenar — numérico si ambos lo son
// (ej. conteos, o un campo de fila que resulta ser un número), si no texto
// (acentos/mayúsculas correctos vía localeCompare "es").
function compararValores(a, b) {
  const aNum = Number(a);
  const bNum = Number(b);
  const esNumerico = a !== "" && b !== "" && !isNaN(aNum) && !isNaN(bNum);
  if (esNumerico) return aNum - bNum;
  return String(a).localeCompare(String(b), "es");
}

const COLUMNA_FILA = "__row__";
const COLUMNA_TOTAL = "__total__";

function formatearNumero(valor, aggregation) {
  const num = valor || 0;
  return aggregation === "sum" ? "$" + num.toLocaleString("es-MX", { minimumFractionDigits: 2 }) : num;
}

// Tabla cruzada a partir del resultado de pivotEngine.computePivot(). Si no
// hay colField (result.cols vacío), se ve como lista simple valor/cantidad;
// si hay colField, se arma la tabla cruzada de verdad (filas × columnas).
// Cualquier columna (incluida la de la fila y la de Total) se puede
// ordenar haciendo clic en su título — la fila de "Total" general se queda
// siempre al final, no participa del orden. aggregation ("count"|"sum")
// solo cambia cómo se VE el número (entero vs "$" con decimales) — el
// cálculo ya viene listo desde pivotEngine.computePivot.
export default function PivotResultTable({ result, rowLabel, colLabel, aggregation }) {
  const { rows, cols, matrix, rowTotals, colTotals, grandTotal } = result;
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const esCruzada = cols.length > 0;

  function valorDeOrden(row, columna) {
    if (columna === COLUMNA_FILA) return row;
    if (columna === COLUMNA_TOTAL) {
      return esCruzada ? rowTotals[row] || 0 : (matrix[row] && matrix[row]["_total"]) || 0;
    }
    return (matrix[row] && matrix[row][columna]) || 0;
  }

  const rowsOrdenadas = useMemo(() => {
    if (!sortBy) return rows;
    const copia = [...rows];
    copia.sort((a, b) => {
      const cmp = compararValores(valorDeOrden(a, sortBy), valorDeOrden(b, sortBy));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copia;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, matrix, rowTotals, sortBy, sortDir]);

  function ordenarPor(columna) {
    if (sortBy === columna) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columna);
      setSortDir("asc");
    }
  }

  function HeaderOrdenable({ columna, align, children }) {
    return (
      <TableCell align={align}>
        <TableSortLabel
          active={sortBy === columna}
          direction={sortBy === columna ? sortDir : "asc"}
          onClick={() => ordenarPor(columna)}
        >
          {children}
        </TableSortLabel>
      </TableCell>
    );
  }

  if (!rows.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        No hay datos para esta combinación de filtros.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <HeaderOrdenable columna={COLUMNA_FILA}>{rowLabel || "Valor"}</HeaderOrdenable>
          {esCruzada
            ? cols.map((col) => (
                <HeaderOrdenable key={col} columna={col} align="right">
                  {col}
                </HeaderOrdenable>
              ))
            : null}
          <HeaderOrdenable columna={COLUMNA_TOTAL} align="right">
            {esCruzada ? "Total" : aggregation === "sum" ? "Suma $" : "Cantidad"}
          </HeaderOrdenable>
        </TableRow>
      </TableHead>
      <TableBody>
        {rowsOrdenadas.map((row) => (
          <TableRow key={row}>
            <TableCell>{row}</TableCell>
            {esCruzada
              ? cols.map((col) => (
                  <TableCell key={col} align="right">
                    {formatearNumero((matrix[row] && matrix[row][col]) || 0, aggregation)}
                  </TableCell>
                ))
              : null}
            <TableCell align="right">
              <b>{formatearNumero(esCruzada ? rowTotals[row] : matrix[row]["_total"], aggregation)}</b>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell>
            <b>Total</b>
          </TableCell>
          {esCruzada
            ? cols.map((col) => (
                <TableCell key={col} align="right">
                  <b>{formatearNumero(colTotals[col] || 0, aggregation)}</b>
                </TableCell>
              ))
            : null}
          <TableCell align="right">
            <b>{formatearNumero(grandTotal, aggregation)}</b>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
